import React, { useRef, useState, useMemo, useEffect } from 'react';
import { EditorValue } from 'src/lib/rich-text-editor';
import { FormInput, Modal } from 'src/components/molecules';
import { FormInputDescription } from 'src/components/molecules/FormInput/FormInput.styles';
import {
  FormInputVariant,
  FormState,
  HumanReadableLanguagesForCategories,
} from 'src/types';
import { WYSIWYG } from 'src/components/atomsLegacy';
import {
  CharacterLimitHint,
  StyledHeading,
  FormInputWrapper,
} from './UpdateCategoryModal.styles';
import { DefaultOption } from 'src/components/molecules/DropdownSelect';
import { CategoryType } from 'src/graphql';
import { useIsFeatureEnabled } from 'src/hooks';
import { FeatureFlag } from 'src/types';
import { AtlasMuiThemeProvider } from 'src/theme/atlas/atlasMuiThemeProvider';
import {
  CATEGORY_CONFIGURATION_V2_DESCRIPTION_MAX_LENGTH,
  CATEGORY_CONFIGURATION_V2_NAME_MAX_LENGTH,
} from './helpers';
import {
  checkForDuplicateName,
  getActionBarHelpText,
  getDescriptionEdited,
  getDescriptionEmpty,
  getCategoryNameErrorMessage,
  getNameEdited,
  getNameEmpty,
  showUnsavedChangesWarning,
} from './helpers';

interface UpdateCategoryModalProps {
  currentCategory: CategoryType;
  categoryNames: string[];
  updateCategoryModalOpen: boolean;
  setUpdateCategoryModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setCurrentCategory: React.Dispatch<React.SetStateAction<CategoryType | null>>;
  networkError: boolean;
  setNetworkError: React.Dispatch<React.SetStateAction<boolean>>;
  updateCategoryLocale: (
    id: number,
    locale: string,
    name: string,
    description: string,
  ) => void;
}

const defaultLanguage = { value: 'en-GB', label: 'English' };

export const UpdateCategoryModal = ({
  updateCategoryModalOpen,
  setUpdateCategoryModalOpen,
  currentCategory,
  categoryNames,
  setCurrentCategory,
  updateCategoryLocale,
  networkError,
  setNetworkError,
}: UpdateCategoryModalProps) => {
  const [selectedLanguage, setSelectedLanguage] =
    useState<DefaultOption>(defaultLanguage);

  const placeholder = currentCategory?.locales?.['en-GB']?.name;

  const currentLocale =
    currentCategory.locales?.[selectedLanguage.value as keyof DefaultOption];

  const defaultCategoryNameValue = currentLocale?.name;

  const defaultCategoryDescription = currentLocale?.description;

  const [newCategoryDescriptionHTML, setNewCategoryDescriptionHTML] =
    useState<string>();
  const [newCategoryDescriptionMarkdown, setNewCategoryDescriptionMarkdown] =
    useState<string>();

  const [newCategoryName, setNewCategoryName] = useState<string | undefined>();

  useEffect(() => {
    setNewCategoryName(undefined);
    setNewCategoryDescriptionHTML(undefined);
    setNewCategoryDescriptionMarkdown(undefined);
    setNetworkError(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLanguage.label]);

  const categoryNameRef = useRef<HTMLInputElement | null>(null);

  const isNameEdited = getNameEdited(newCategoryName, defaultCategoryNameValue);

  const isDescriptionEdited = getDescriptionEdited(
    newCategoryDescriptionHTML,
    defaultCategoryDescription,
  );

  const isNameEmpty = getNameEmpty(
    isNameEdited,
    defaultCategoryNameValue,
    newCategoryName,
  );

  const isNewCategoryDescriptionEmpty = useMemo(() => {
    if (newCategoryDescriptionMarkdown === undefined) {
      return undefined;
    }

    return ['', '\u200b'].includes(newCategoryDescriptionMarkdown.trim());
  }, [newCategoryDescriptionMarkdown]);

  const categoryNameCharCount = useMemo(() => {
    if (newCategoryName !== undefined) {
      return newCategoryName.length;
    }
    return defaultCategoryNameValue?.length ?? 0;
  }, [newCategoryName, defaultCategoryNameValue]);

  const descriptionMarkdownCharCount = useMemo(() => {
    if (newCategoryDescriptionMarkdown !== undefined) {
      return newCategoryDescriptionMarkdown.length;
    }
    if (!defaultCategoryDescription?.trim()) {
      return 0;
    }
    try {
      return EditorValue.createFromString(
        defaultCategoryDescription,
        'html',
      ).toString('markdown').length;
    } catch {
      return defaultCategoryDescription.length;
    }
  }, [newCategoryDescriptionMarkdown, defaultCategoryDescription]);

  const isDescriptionEmpty = getDescriptionEmpty(
    isDescriptionEdited,
    defaultCategoryDescription,
    isNewCategoryDescriptionEmpty,
  );

  const duplicateName = isNameEdited
    ? checkForDuplicateName(newCategoryName, categoryNames)
    : false;

  const showCategoryNameError =
    (isNameEmpty && !isDescriptionEmpty) || duplicateName;

  const canUpdate =
    (isNameEdited || isDescriptionEdited) &&
    !showCategoryNameError &&
    !isNameEmpty &&
    !duplicateName;

  const errorMessage = getCategoryNameErrorMessage(
    duplicateName,
    showCategoryNameError,
    selectedLanguage.label,
  );

  const showWarning = showUnsavedChangesWarning(
    isNameEdited,
    isDescriptionEdited,
  );

  const actionBarHelpText = getActionBarHelpText(networkError, showWarning);
  const isAtlasThemeEnabled = useIsFeatureEnabled(FeatureFlag.ATLAS_THEME);

  useEffect(() => {
    if (isNameEdited || isDescriptionEdited) {
      setNetworkError(false);
    }
  }, [isNameEdited, isDescriptionEdited, setNetworkError]);

  const modalContent = (
    <Modal
      size="large"
      title="Update category details"
      description="We encourage you to ensure the appropriate reviews and sign offs are in place for all policy language."
      open={updateCategoryModalOpen}
      setOpen={() => {
        setNetworkError(false);
        setUpdateCategoryModalOpen(false);
      }}
      onCancel={() => {
        setNetworkError(false);
        setUpdateCategoryModalOpen(false);
      }}
      onConfirm={() => {
        setNetworkError(false);

        const newCategoryName = categoryNameRef.current?.value;

        setCurrentCategory({
          ...currentCategory,
          locales: {
            ...currentCategory.locales,
            [selectedLanguage.value as keyof DefaultOption]: {
              ...currentLocale,
              name: newCategoryName ?? defaultCategoryNameValue,
              description:
                newCategoryDescriptionHTML ?? defaultCategoryDescription ?? '',
            },
          },
        });

        updateCategoryLocale(
          currentCategory.id,
          selectedLanguage.value as keyof DefaultOption,
          newCategoryName ?? defaultCategoryNameValue,
          newCategoryDescriptionHTML ?? defaultCategoryDescription ?? '',
        );
      }}
      includeClose={true}
      isConfirmButtonDisabled={!canUpdate}
      confirmButtonText="Update details"
      actionBarHelpText={actionBarHelpText}
      data-testid="update-category-modal"
    >
      <FormInputWrapper>
        <FormInput
          label="1. Category language"
          description="Select your category language"
          variant={FormInputVariant.DROPDOWN}
          menuPlacement="bottom"
          options={HumanReadableLanguagesForCategories.sort((a, b) =>
            a[1].localeCompare(b[1]),
          ).map(([value, label]) => ({
            value,
            label,
          }))}
          defaultValue={defaultLanguage}
          isMulti={false}
          onChange={(selected) => {
            selected
              ? setSelectedLanguage(selected as DefaultOption)
              : undefined;
            setNewCategoryName(undefined);
            setNewCategoryDescriptionHTML(undefined);
            setNewCategoryDescriptionMarkdown(undefined);
          }}
          data-testid="category-language-dropdown"
        />
      </FormInputWrapper>
      <FormInputWrapper>
        <FormInput
          key={selectedLanguage.label}
          label="2. Category name"
          description={
            showCategoryNameError
              ? undefined
              : `Provide the translated category name as it should appear in ${selectedLanguage.label}`
          }
          variant={FormInputVariant.INPUT}
          state={showCategoryNameError ? FormState.ERROR : FormState.DEFAULT}
          placeholder={placeholder}
          defaultValue={defaultCategoryNameValue}
          forwardRef={categoryNameRef}
          type="text"
          isRequired
          maxLength={CATEGORY_CONFIGURATION_V2_NAME_MAX_LENGTH}
          onChange={(e) => setNewCategoryName(e.target.value)}
          data-testid="category-name-input"
          helpText={errorMessage}
        />
        <CharacterLimitHint
          $atLimit={
            categoryNameCharCount >= CATEGORY_CONFIGURATION_V2_NAME_MAX_LENGTH
          }
          data-testid="category-name-char-count"
        >
          {categoryNameCharCount} / {CATEGORY_CONFIGURATION_V2_NAME_MAX_LENGTH}
        </CharacterLimitHint>
      </FormInputWrapper>

      <FormInputWrapper>
        <StyledHeading>3. Category description</StyledHeading>
        <FormInputDescription hasMarginTop>
          Maximum {CATEGORY_CONFIGURATION_V2_DESCRIPTION_MAX_LENGTH} characters.
        </FormInputDescription>
        <WYSIWYG
          key={selectedLanguage.label}
          onChange={({ html, markdown }) => {
            setNewCategoryDescriptionHTML(html);
            setNewCategoryDescriptionMarkdown(markdown);
          }}
          value={defaultCategoryDescription ?? ''}
          placeholder={`Enter the translated category description in ${selectedLanguage.label}, this is what reporters will read to understand the category more`}
          reducedMargin
          framed
          formatType={'html'}
          maxMarkdownLength={CATEGORY_CONFIGURATION_V2_DESCRIPTION_MAX_LENGTH}
        />
        <CharacterLimitHint
          $atLimit={
            descriptionMarkdownCharCount >=
            CATEGORY_CONFIGURATION_V2_DESCRIPTION_MAX_LENGTH
          }
          data-testid="category-description-char-count"
        >
          {descriptionMarkdownCharCount} /{' '}
          {CATEGORY_CONFIGURATION_V2_DESCRIPTION_MAX_LENGTH}
        </CharacterLimitHint>
      </FormInputWrapper>
    </Modal>
  );

  return isAtlasThemeEnabled ? (
    <AtlasMuiThemeProvider>{modalContent}</AtlasMuiThemeProvider>
  ) : (
    modalContent
  );
};
